import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';
import { API_URL } from '../constants/urls';

// Types
export interface OKRRelationshipCreate {
  target_type: 'okr' | 'key_result';
  target_id: string;
  relationship_type: 'contributes_to' | 'depends_on' | 'aligned_with';
  contribution_notes?: string;
}

export interface OKRRelationship {
  id: string;
  okr_id: string;
  relationship_type: string;
  related_object_id: string;
  contribution_notes?: string;
  created_at: string;
}

export interface OKRHierarchy {
  okr: {
    id: string;
    objective: string;
    description?: string;
    status: string;
    is_corporate: boolean;
  };
  ancestors: Array<{
    id: string;
    objective: string;
    level: number;
  }>;
  children: Array<{
    id: string;
    objective: string;
    description?: string;
    status: string;
    key_results_count: number;
  }>;
}

// OKR Relationships Service
export const okrRelationshipsService = {
  // Set or remove parent OKR
  async setParent(
    okrId: string,
    parentOkrId: string | null,
    organizationId: string,
  ): Promise<any> {
    const response = await axios.post(
      `${API_URL}/okrs/${okrId}/set-parent?organization_id=${organizationId}`,
      null,
      {
        headers: getAuthHeaders(),
        params: { parent_okr_id: parentOkrId },
      },
    );
    return response.data;
  },

  // Get OKR hierarchy
  async getHierarchy(
    okrId: string,
    organizationId: string,
    includeChildren = true,
    includeAncestors = true,
  ): Promise<OKRHierarchy> {
    const response = await axios.get(`${API_URL}/okrs/${okrId}/hierarchy`, {
      headers: getAuthHeaders(),
      params: {
        organization_id: organizationId,
        include_children: includeChildren,
        include_ancestors: includeAncestors,
      },
    });
    return response.data;
  },

  // Create a cross-functional relationship
  async createRelationship(
    okrId: string,
    relationship: OKRRelationshipCreate,
    organizationId: string,
  ): Promise<OKRRelationship> {
    const response = await axios.post(
      `${API_URL}/okrs/${okrId}/relationships?organization_id=${organizationId}`,
      relationship,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  // List relationships for an OKR
  async listRelationships(
    okrId: string,
    organizationId: string,
  ): Promise<OKRRelationship[]> {
    const response = await axios.get(
      `${API_URL}/okrs/${okrId}/relationships?organization_id=${organizationId}`,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  // Delete a relationship
  async deleteRelationship(
    okrId: string,
    relationshipId: string,
    organizationId: string,
  ): Promise<void> {
    await axios.delete(
      `${API_URL}/okrs/${okrId}/relationships/${relationshipId}?organization_id=${organizationId}`,
      { headers: getAuthHeaders() },
    );
  },

  // Get all OKRs that could be relationship targets
  async getPotentialTargets(
    organizationId: string,
    excludeOkrId?: string,
  ): Promise<any[]> {
    const response = await axios.get(
      `${API_URL}/okrs?organization_id=${organizationId}&include_children=false&include_relationships=false`,
      { headers: getAuthHeaders() },
    );

    // Filter out the current OKR if provided
    const okrs = response.data;
    return excludeOkrId
      ? okrs.filter((okr: any) => okr.id !== excludeOkrId)
      : okrs;
  },
};
